import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Image as ImageIcon } from 'lucide-react'
import { AdImageWithTag } from '@magnetar/admin-api-interfaces'
import { useAdSuppliers } from '@/hooks/useAdSuppliers'
import { useAdImages } from '@/hooks/useAdImages'

const getImageUrl = (fileUuid: string) => `${import.meta.env.VITE_API_URL}/api/files/download/${fileUuid}`

interface AddAdImageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * 限制只能挑選此 provider 底下供應商的圖片。
   * 系統層級批次 (provider_id 為 null) 則不限制，可從所有供應商挑選。
   */
  providerId?: number
  existingImageIds?: Set<number>
  onAdd: (selectedImages: AdImageWithTag[]) => void
}

export default function AddAdImageDialog({
  open,
  onOpenChange,
  providerId,
  existingImageIds = new Set(),
  onAdd,
}: AddAdImageDialogProps) {
  const [selectedSupplierId, setSelectedSupplierId] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const { adSuppliers, isLoading: isSuppliersLoading } = useAdSuppliers({
    page: 1,
    pageSize: 1000,
    providerId,
  })

  const supplierId = selectedSupplierId ? Number(selectedSupplierId) : undefined
  const { adImages, isLoading: isImagesLoading } = useAdImages(supplierId)

  const handleToggle = (imageId: number) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId)
    } else {
      newSelected.add(imageId)
    }
    setSelectedIds(newSelected)
  }

  const handleAdd = () => {
    const selectedImages = adImages.filter((image) => selectedIds.has(image.ad_image_id))
    onAdd(selectedImages)
    setSelectedIds(new Set())
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>選擇廣告圖片</DialogTitle>
          <DialogDescription>先選擇供應商，再選擇要加入批次的廣告圖片</DialogDescription>
        </DialogHeader>

        <div className="px-6">
          <Select value={selectedSupplierId} onValueChange={setSelectedSupplierId}>
            <SelectTrigger>
              <SelectValue placeholder="選擇供應商" />
            </SelectTrigger>
            <SelectContent>
              {isSuppliersLoading ? (
                <SelectItem value="loading" disabled>
                  載入中...
                </SelectItem>
              ) : (
                adSuppliers?.map((supplier) => (
                  <SelectItem key={supplier.supplier_id} value={String(supplier.supplier_id)}>
                    {supplier.supplier_name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!supplierId ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">請先選擇供應商</p>
            </div>
          ) : isImagesLoading ? (
            <div className="text-center py-8 text-muted-foreground">載入中...</div>
          ) : adImages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">此供應商尚未上傳廣告圖片</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {adImages.map((image) => {
                const alreadyInBatch = existingImageIds.has(image.ad_image_id)
                return (
                  <div
                    key={image.ad_image_id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      alreadyInBatch ? 'opacity-50' : 'hover:bg-muted/50 cursor-pointer'
                    }`}
                    onClick={() => !alreadyInBatch && handleToggle(image.ad_image_id)}
                  >
                    <Checkbox
                      checked={selectedIds.has(image.ad_image_id)}
                      disabled={alreadyInBatch}
                      onCheckedChange={() => handleToggle(image.ad_image_id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-0.5 shrink-0"
                    />
                    <img
                      src={getImageUrl(image.file_uuid)}
                      alt={`圖片 ${image.ad_image_id}`}
                      className="w-16 h-16 object-cover rounded shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">#{image.ad_image_id}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="secondary" className="text-xs shrink-0">
                          <ImageIcon className="mr-1 h-3 w-3" />
                          {image.play_duration_ms
                            ? `${Math.round(image.play_duration_ms / 1000)} 秒`
                            : '未設定時長'}
                        </Badge>
                        {image.tag_ad && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            標記廣告：#{image.tag_ad.advertisement_id} {image.tag_ad.title || ''}
                          </Badge>
                        )}
                        {alreadyInBatch && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            已在批次中
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 pb-6">
          <div className="flex items-center justify-between w-full">
            <p className="text-sm text-muted-foreground">已選擇 {selectedIds.size} 張圖片</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button onClick={handleAdd} disabled={selectedIds.size === 0}>
                加入 {selectedIds.size > 0 && `(${selectedIds.size})`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
